import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../assets/spacem8-logo.png";
import StorageFileLink from "../components/StorageFileLink";
import { supabase } from "../lib/supabase";
import { useToast } from "../contexts/ToastContext";
import { logActivity } from "../lib/auditLog";
import { buildStorageReference } from "../lib/storageReferences";
import {
  AlertTriangle,
  ArrowUpRight,
  BrainCircuit,
  Building2,
  CalendarDays,
  ChartNoAxesCombined,
  FileText,
  House,
  MessageSquareMore,
  Moon,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  Sparkles,
  Sun,
  Users,
  Wallet,
  Wrench,
  CheckCircle2,
  Clock3,
  ShieldAlert,
  Upload,
  X,
} from "lucide-react";

function Button({ children, className = "", variant = "default", ...props }) {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap transition-all font-medium disabled:pointer-events-none disabled:opacity-50";

  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white hover:bg-orange-400 shadow-sm";

  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

const navItems = [
  { label: "Dashboard", icon: House, path: "/" },
  { label: "Properties", icon: Building2, path: "/properties" },
  { label: "Tenants", icon: Users, path: "/tenants" },
  { label: "Payments", icon: Wallet, path: "/payments" },
  { label: "Invoices", icon: FileText, path: "/invoices" },
  { label: "Maintenance", icon: Wrench, path: "/maintenance" },
  { label: "Messages", icon: MessageSquareMore, path: "/messages" },
  { label: "Reports", icon: ChartNoAxesCombined, path: "/reports" },
  { label: "AI Assistant", icon: BrainCircuit, path: "/ai-assistant" },
  { label: "Settings", icon: Settings, path: "/settings" },
];

const maintenanceItems = [
  {
    id: "MT-001",
    issue: "Water leakage in Unit B-12",
    property: "Urban Crest Apartments",
    unit: "B-12",
    category: "Plumbing",
    priority: "High",
    status: "In progress",
    assigned: "Kamau Plumbing",
    reportedOn: "2026-04-02",
  },
  {
    id: "MT-002",
    issue: "Hallway lighting replacement",
    property: "Skyline Residency",
    unit: "Block A",
    category: "Electrical",
    priority: "Medium",
    status: "Open",
    assigned: "In-house Team",
    reportedOn: "2026-04-03",
  },
  {
    id: "MT-003",
    issue: "Gate sensor fault",
    property: "Blue Haven Suites",
    unit: "Main Gate",
    category: "Security",
    priority: "Low",
    status: "Scheduled",
    assigned: "SecureTech",
    reportedOn: "2026-04-01",
  },
  {
    id: "MT-004",
    issue: "Ceiling paint peeling",
    property: "Cedar Point Residences",
    unit: "D-08",
    category: "Finishes",
    priority: "Medium",
    status: "Completed",
    assigned: "Apex Interiors",
    reportedOn: "2026-03-29",
  },
  {
    id: "MT-005",
    issue: "Drain blockage in parking bay",
    property: "Marina View Court",
    unit: "Parking Level 1",
    category: "Drainage",
    priority: "High",
    status: "Open",
    assigned: "Rapid Works",
    reportedOn: "2026-04-04",
  },
  {
    id: "MT-006",
    issue: "Door lock replacement",
    property: "Rivergate Lofts",
    unit: "R-03",
    category: "Access",
    priority: "Low",
    status: "Completed",
    assigned: "BuildCare",
    reportedOn: "2026-03-30",
  },
];

function buildTicketId(ticketCount) {
  return `MT-${String(ticketCount + 1).padStart(3, "0")}`;
}

function mapTicketFromSupabase(ticket) {
  return {
    id: ticket.id,
    issue: ticket.issue,
    property: ticket.property,
    unit: ticket.unit,
    category: ticket.category,
    priority: ticket.priority,
    status: ticket.status,
    assigned: ticket.assigned,
    reportedOn: ticket.reported_on,
    supportingFiles: ticket.supporting_files,
  };
}

function priorityClasses(priority) {
  if (priority === "High") return "bg-rose-100 text-rose-700";
  if (priority === "Medium") return "bg-amber-100 text-amber-800";
  return "bg-sky-100 text-sky-700";
}

function statusClasses(status) {
  if (status === "Completed") return "bg-emerald-100 text-emerald-700";
  if (status === "In progress") return "bg-sky-100 text-sky-700";
  if (status === "Scheduled") return "bg-indigo-100 text-indigo-700";
  return "bg-amber-100 text-amber-800";
}

const MAINTENANCE_DOCUMENT_BUCKET = "maintenance-documents";

function parseFileLinks(value) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return String(value)
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((url) => ({ name: url.split("/").pop(), url }));
  }
}

function getMaintenanceFilePath(file) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  return `maintenance/${Date.now()}-${safeName}`;
}

async function uploadMaintenanceFiles(files) {
  const uploadedFiles = [];

  for (const file of files) {
    const path = getMaintenanceFilePath(file);
    const { error } = await supabase.storage
      .from(MAINTENANCE_DOCUMENT_BUCKET)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) throw error;

    uploadedFiles.push({
      name: file.name,
      url: buildStorageReference(MAINTENANCE_DOCUMENT_BUCKET, path),
      type: file.type,
      size: file.size,
    });
  }

  return uploadedFiles;
}

function CreateTicketModal({ isOpen, onClose, onCreated, isDark, ticketCount }) {
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    issue: "",
    property: "",
    unit: "",
    category: "Plumbing",
    priority: "Medium",
    status: "Open",
    assigned: "",
    reportedOn: today,
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      issue: "",
      property: "",
      unit: "",
      category: "Plumbing",
      priority: "Medium",
      status: "Open",
      assigned: "",
      reportedOn: today,
    });
    setSelectedFiles([]);
    setSaving(false);
  }, [isOpen, today]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchPropertiesAndVendors = async () => {
      setLoadingProperties(true);

      const [
        { data: propertyData, error: propertyError },
        { data: vendorData, error: vendorError },
      ] = await Promise.all([
        supabase
          .from("properties")
          .select("id, name, location")
          .order("name", { ascending: true }),
        supabase
          .from("vendors")
          .select("id, name, service_category, status")
          .neq("status", "Inactive")
          .order("name", { ascending: true }),
      ]);

      if (propertyError) {
        console.error("Error fetching properties for maintenance ticket:", propertyError);
        setProperties([]);
      } else {
        setProperties(propertyData || []);
      }

      if (vendorError) {
        console.error("Error fetching vendors for maintenance ticket:", vendorError);
        setVendors([]);
      } else {
        setVendors(vendorData || []);
      }

      setLoadingProperties(false);
    };

    fetchPropertiesAndVendors();
  }, [isOpen]);

  if (!isOpen) return null;

  const inputClass = isDark
    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
    : "border-slate-200 bg-white text-slate-900";

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    let uploadedFiles = [];

    try {
      uploadedFiles = selectedFiles.length ? await uploadMaintenanceFiles(selectedFiles) : [];
    } catch (error) {
      setSaving(false);
      console.error("Maintenance file upload error:", error);
      toast.error(
        "Maintenance attachment upload failed",
        "Please check the selected files and try again."
      );
      return;
    }

    const wasCreated = await onCreated({
      id: buildTicketId(ticketCount),
      issue: form.issue,
      property: form.property,
      unit: form.unit,
      category: form.category,
      priority: form.priority,
      status: form.status,
      assigned: form.assigned,
      reportedOn: form.reportedOn,
      supportingFiles: uploadedFiles.length ? JSON.stringify(uploadedFiles) : null,
    });

    setSaving(false);

    if (wasCreated) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Create Ticket
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Add a maintenance issue to the operations queue.
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Input
              placeholder="Issue"
              value={form.issue}
              onChange={(e) => update("issue", e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <Select
            value={form.property}
            onChange={(e) => update("property", e.target.value)}
            className={inputClass}
            required
          >
            <option value="">
              {loadingProperties ? "Loading properties..." : "Select property"}
            </option>
            {properties.map((property) => (
              <option key={property.id} value={property.name}>
                {property.name}
                {property.location ? ` - ${property.location}` : ""}
              </option>
            ))}
          </Select>

          <Input
            placeholder="Unit"
            value={form.unit}
            onChange={(e) => update("unit", e.target.value)}
            className={inputClass}
            required
          />

          <Select
            value={form.category}
            onChange={(e) => update("category", e.target.value)}
            className={inputClass}
          >
            <option>Plumbing</option>
            <option>Electrical</option>
            <option>Security</option>
            <option>Finishes</option>
            <option>Drainage</option>
            <option>Access</option>
            <option>General</option>
          </Select>

          <Select
            value={form.priority}
            onChange={(e) => update("priority", e.target.value)}
            className={inputClass}
          >
            <option>High</option>
            <option>Medium</option>
            <option>Low</option>
          </Select>

          <Select
            value={form.status}
            onChange={(e) => update("status", e.target.value)}
            className={inputClass}
          >
            <option>Open</option>
            <option>In progress</option>
            <option>Scheduled</option>
            <option>Completed</option>
          </Select>

          <Select
            value={form.assigned}
            onChange={(e) => update("assigned", e.target.value)}
            className={inputClass}
            required
          >
            <option value="">Select vendor / assignee</option>
            <option value="In-house Team">In-house Team</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.name}>
                {vendor.name} - {vendor.service_category}
              </option>
            ))}
          </Select>

          <div className="md:col-span-2">
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Reported on
            </label>
            <Input
              type="date"
              value={form.reportedOn}
              onChange={(e) => update("reportedOn", e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div className={`rounded-2xl border p-4 md:col-span-2 ${isDark ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Supporting files
            </label>
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
              className={`block w-full text-sm ${isDark ? "text-slate-300" : "text-slate-700"}`}
            />
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Attach issue photos, contractor quotes, supplier invoices, or completion evidence.
            </p>
            {selectedFiles.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedFiles.map((file) => (
                  <span
                    key={`${file.name}-${file.size}`}
                    className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700"
                  >
                    <Upload className="mr-1 h-3 w-3" />
                    {file.name}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? "Creating..." : "Create Ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Sidebar({ isDark }) {
  return (
    <aside
      className={`hidden xl:flex xl:w-72 xl:flex-col xl:rounded-[2rem] xl:border xl:p-5 xl:shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-6 px-2">
        <div
          className={`flex items-center justify-center rounded-3xl border px-6 py-6 shadow-sm transition-all hover:shadow-md ${
            isDark
              ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
              : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
          }`}
        >
          <img
            src={logo}
            alt="SpaceM8"
            className="h-20 w-auto cursor-pointer object-contain transition-all duration-300 hover:scale-110"
            onClick={() => (window.location.href = "/")}
          />
        </div>

        <p className={`mt-4 text-center text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          Where Property Meets Intelligence
        </p>
      </div>

      <nav className="mt-1 space-y-2">
        {navItems.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.label}
              to={item.path}
              className={({ isActive }) =>
                `flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all ${
                  isActive
                    ? isDark
                      ? "bg-slate-800 text-white ring-1 ring-slate-700"
                      : "bg-sky-50 text-slate-900 ring-1 ring-sky-100"
                    : isDark
                    ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                    : "text-slate-600 hover:bg-gradient-to-br hover:from-white hover:to-sky-50 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`h-5 w-5 ${
                      isActive
                        ? isDark
                          ? "text-sky-400"
                          : "text-sky-700"
                        : isDark
                        ? "text-slate-500"
                        : "text-slate-400"
                    }`}
                  />
                  <span className="text-sm font-medium">{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div
        className={`mt-6 rounded-3xl border p-5 ${
          isDark
            ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
            : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
        }`}
      >
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Maintenance Summary
        </p>
        <div className="mt-4 space-y-4">
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Operational health</p>
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>Stable</p>
          </div>
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Urgent attention</p>
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>2 items</p>
          </div>
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Flagged property</p>
            <p className="text-lg font-semibold text-sky-600">Urban Crest Apartments</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

function ViewTicketModal({ isOpen, ticket, onClose, isDark }) {
  if (!isOpen || !ticket) return null;

  const attachments = parseFileLinks(ticket.supportingFiles);
  const details = [
    { label: "Ticket ID", value: ticket.id },
    { label: "Property", value: ticket.property },
    { label: "Unit", value: ticket.unit },
    { label: "Category", value: ticket.category },
    { label: "Priority", value: ticket.priority },
    { label: "Status", value: ticket.status },
    { label: "Assigned to", value: ticket.assigned },
    { label: "Reported on", value: ticket.reportedOn },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {ticket.issue}
              </h2>
              <Badge className={priorityClasses(ticket.priority)}>{ticket.priority}</Badge>
              <Badge className={statusClasses(ticket.status)}>{ticket.status}</Badge>
            </div>
            <p className={`mt-2 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Maintenance ticket details.
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {details.map((detail) => (
            <div
              key={detail.label}
              className={`min-w-0 rounded-2xl border px-4 py-3 ${
                isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
              }`}
            >
              <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {detail.label}
              </p>
              <p className={`mt-1 break-words font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {detail.value || "Not set"}
              </p>
            </div>
          ))}
        </div>

        <div className={`mt-5 rounded-2xl border p-4 ${isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"}`}>
          <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            Supporting files
          </p>
          {attachments.length === 0 ? (
            <p className={`mt-2 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              No files attached.
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {attachments.map((file, index) => (
                <StorageFileLink
                  key={`${file.url}-${index}`}
                  reference={file.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700"
                >
                  {file.name || `File ${index + 1}`}
                </StorageFileLink>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function UpdateStatusModal({ isOpen, ticket, onClose, onSaved, isDark }) {
  const toast = useToast();
  const [status, setStatus] = useState("Open");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status || "Open");
    }
  }, [ticket, isOpen]);

  if (!isOpen || !ticket) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const { data, error } = await supabase
      .from("maintenance_tickets")
      .update({ status })
      .eq("id", ticket.id)
      .select()
      .single();

    setSaving(false);

    if (error) {
      console.error("Update maintenance status error:", error);
      toast.error("Ticket status update failed", "Please try updating the status again.");
      return;
    }

    await logActivity({
      action: "Updated maintenance ticket status",
      entityType: "maintenance_ticket",
      entityId: ticket.id,
      entityLabel: ticket.issue,
      details: { previousStatus: ticket.status, nextStatus: status },
    });

    onSaved(mapTicketFromSupabase(data));
    toast.success("Ticket updated", `${ticket.id} is now marked as ${status}.`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-lg rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Update Status
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {ticket.id} - {ticket.issue}
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            Status
          </label>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
          >
            <option>Open</option>
            <option>In progress</option>
            <option>Scheduled</option>
            <option>Completed</option>
          </Select>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? "Updating..." : "Update Status"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MaintenanceCard({ item, index, isDark, onView, onUpdateStatus }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className={`rounded-[2rem] border shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {item.issue}
              </h3>
              <Badge className={priorityClasses(item.priority)}>{item.priority}</Badge>
              <Badge className={statusClasses(item.status)}>{item.status}</Badge>
            </div>

            <div className={`mt-2 flex flex-wrap items-center gap-3 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              <span>{item.id}</span>
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-4 w-4" />
                {item.property}
              </span>
              <span>{item.unit}</span>
            </div>
          </div>

          <button
            className={`rounded-xl border p-2 transition ${
              isDark
                ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
            }`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div
            className={`rounded-2xl border px-4 py-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Category
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {item.category}
            </p>
          </div>

          <div
            className={`rounded-2xl border px-4 py-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Assigned to
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {item.assigned}
            </p>
          </div>

          <div
            className={`rounded-2xl border px-4 py-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Reported on
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {item.reportedOn}
            </p>
          </div>

          <div
            className={`rounded-2xl border px-4 py-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Severity
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {item.priority}
            </p>
          </div>
        </div>

        <div
          className={`mt-5 flex flex-col gap-3 border-t pt-4 md:flex-row md:items-center md:justify-between ${
            isDark ? "border-slate-800" : "border-slate-200"
          }`}
        >
          <div className={`flex flex-wrap items-center gap-4 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            <span className="inline-flex items-center gap-1.5">
              <Clock3 className="h-4 w-4" />
              SLA monitored
            </span>
            <span className="inline-flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              Escalation ready
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onView(item)}
              className={`rounded-2xl px-4 py-2 text-sm ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : ""
              }`}
            >
              View ticket
            </Button>
            <Button
              onClick={() => onUpdateStatus(item)}
              className="rounded-2xl px-4 py-2 text-sm"
            >
              Update status
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function SpaceM8MaintenancePage({ isDark, setIsDark }) {
  const location = useLocation();
  const toast = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [escalationsOnly, setEscalationsOnly] = useState(false);
  const [viewingTicket, setViewingTicket] = useState(null);
  const [updatingTicket, setUpdatingTicket] = useState(null);

  const fetchTickets = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from("maintenance_tickets")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching maintenance tickets:", error);
      setTickets(maintenanceItems);
    } else {
      setTickets((data || []).map(mapTicketFromSupabase));
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    const nextState = location.state;

    if (!nextState) return;

    if (nextState.initialQuery) {
      setQuery(nextState.initialQuery);
    }

    if (nextState.initialStatusFilter) {
      setStatusFilter(nextState.initialStatusFilter);
    }

    if (typeof nextState.initialEscalationsOnly === "boolean") {
      setEscalationsOnly(nextState.initialEscalationsOnly);
    }

    window.history.replaceState({}, document.title);
  }, [location.state]);

  const handleCreateTicket = async (ticket) => {
    const payload = {
      id: ticket.id,
      issue: ticket.issue,
      property: ticket.property,
      unit: ticket.unit,
      category: ticket.category,
      priority: ticket.priority,
      status: ticket.status,
      assigned: ticket.assigned,
      reported_on: ticket.reportedOn,
      supporting_files: ticket.supportingFiles,
    };

    const { data, error } = await supabase
      .from("maintenance_tickets")
      .insert([payload])
      .select()
      .single();

    if (error) {
      console.error("Create maintenance ticket error:", error);
      toast.error(
        "Maintenance ticket creation failed",
        "Please review the ticket details and try again."
      );
      return false;
    }

    await logActivity({
      action: "Created maintenance ticket",
      entityType: "maintenance_ticket",
      entityId: data.id,
      entityLabel: data.issue,
      details: payload,
    });

    setTickets((prev) => [mapTicketFromSupabase(data), ...prev]);
    toast.success("Maintenance ticket created", `${data.id} has been added to the operations queue.`);
    return true;
  };

  const handleStatusSaved = (updatedTicket) => {
    setTickets((prev) =>
      prev.map((ticket) => (ticket.id === updatedTicket.id ? updatedTicket : ticket))
    );
  };

  const totals = useMemo(() => {
    const open = tickets.filter((item) => item.status === "Open").length;
    const inProgress = tickets.filter((item) => item.status === "In progress").length;
    const scheduled = tickets.filter((item) => item.status === "Scheduled").length;
    const completed = tickets.filter((item) => item.status === "Completed").length;

    return {
      open,
      inProgress,
      scheduled,
      completed,
      totalTickets: tickets.length,
    };
  }, [tickets]);

  const filteredItems = useMemo(() => {
    return tickets.filter((item) => {
      const matchesQuery =
        item.issue.toLowerCase().includes(query.toLowerCase()) ||
        item.property.toLowerCase().includes(query.toLowerCase()) ||
        item.unit.toLowerCase().includes(query.toLowerCase()) ||
        item.id.toLowerCase().includes(query.toLowerCase()) ||
        item.category.toLowerCase().includes(query.toLowerCase());

      const matchesStatus = statusFilter === "All" || item.status === statusFilter;
      const matchesEscalation =
        !escalationsOnly ||
        (item.priority === "High" && item.status !== "Completed");

      return matchesQuery && matchesStatus && matchesEscalation;
    });
  }, [tickets, query, statusFilter, escalationsOnly]);

  return (
    <div
      className={`min-h-screen ${
        isDark
          ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white"
          : "bg-gradient-to-br from-[#f8fbff] via-[#eef6ff] to-[#f3f9ff] text-slate-900"
      }`}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute inset-0 ${
            isDark
              ? "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.08),_transparent_22%),linear-gradient(to_bottom_right,_#020617,_#0f172a,_#020617)]"
              : "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.10),_transparent_24%),linear-gradient(to_bottom_right,_#f8fbff,_#f6f9fc,_#eef5fb)]"
          }`}
        />
      </div>

      <div className="relative mx-auto flex max-w-[1600px] gap-6 p-4 md:p-6 xl:p-8">
        <Sidebar isDark={isDark} />

        <main className="flex-1">
          <motion.header
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-[2rem] border p-5 shadow-sm ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sky-700">
                  <Sparkles className="h-4 w-4" />
                  <p className="text-xs font-semibold uppercase tracking-[0.2em]">
                    Operations Control
                  </p>
                </div>
                <h1 className={`mt-2 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  Maintenance
                </h1>
                <p className={`mt-2 max-w-2xl text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Track open issues, assigned vendors, maintenance progress, and property risk from one executive operations workspace.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  className={`h-12 rounded-2xl px-4 ${
                    isDark
                      ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => setIsDark(!isDark)}
                >
                  {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                  {isDark ? "Light" : "Dark"}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setEscalationsOnly((current) => !current)}
                  className={`h-12 rounded-2xl px-4 ${
                    escalationsOnly
                      ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                      : isDark
                      ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <ShieldAlert className="mr-2 h-4 w-4" />
                  {escalationsOnly ? "All Tickets" : "Escalations"}
                </Button>

                <Button
                  onClick={() => setIsCreateOpen(true)}
                  className="h-12 rounded-2xl px-5"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Ticket
                </Button>
              </div>
            </div>
          </motion.header>

          <section className="mt-6 grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
            {[
              {
                label: "Open",
                value: `${totals.open}`,
                icon: AlertTriangle,
                tone: "bg-amber-100 text-amber-800",
              },
              {
                label: "In progress",
                value: `${totals.inProgress}`,
                icon: Wrench,
                tone: "bg-sky-100 text-sky-700",
              },
              {
                label: "Scheduled",
                value: `${totals.scheduled}`,
                icon: CalendarDays,
                tone: "bg-indigo-100 text-indigo-700",
              },
              {
                label: "Completed",
                value: `${totals.completed}`,
                icon: CheckCircle2,
                tone: "bg-emerald-100 text-emerald-700",
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`rounded-[2rem] border p-5 shadow-md transition-shadow hover:shadow-lg ${
                    isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {item.label}
                      </p>
                      <h3 className={`mt-3 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                        {item.value}
                      </h3>
                      <div
                        className={`mt-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm ${
                          isDark ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-600"
                        }`}
                      >
                        <ArrowUpRight className="h-4 w-4 text-sky-600" />
                        Operations visible
                      </div>
                    </div>
                    <div className={`rounded-2xl p-3 ${item.tone}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </section>

          <section
            className={`mt-6 rounded-[2rem] border p-4 shadow-sm md:p-5 ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
                <div className="relative w-full max-w-md">
                  <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setEscalationsOnly(false);
                    }}
                    placeholder="Search by issue, property, unit, category, or ticket ID..."
                    className={`h-12 pl-11 pr-4 ${
                      isDark
                        ? "border-slate-700 bg-slate-900 text-white placeholder:text-slate-400"
                        : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                    }`}
                  />
                </div>

                <div className="flex items-center gap-2">
                  {["All", "Open", "In progress", "Scheduled", "Completed"].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setEscalationsOnly(false);
                      }}
                      className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                        statusFilter === status
                          ? "bg-sky-100 text-sky-700"
                          : isDark
                          ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                          : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mt-6 space-y-5">
            {loading ? (
              <div
                className={`rounded-3xl border p-8 text-center ${
                  isDark ? "border-slate-800 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600"
                }`}
              >
                Loading maintenance tickets...
              </div>
            ) : filteredItems.length === 0 ? (
              <div
                className={`rounded-3xl border p-8 text-center ${
                  isDark ? "border-slate-800 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600"
                }`}
              >
                No maintenance tickets found.
              </div>
            ) : (
              filteredItems.map((item, index) => (
                <MaintenanceCard
                  key={item.id}
                  item={item}
                  index={index}
                  isDark={isDark}
                  onView={setViewingTicket}
                  onUpdateStatus={setUpdatingTicket}
                />
              ))
            )}
          </section>
        </main>
      </div>

      <CreateTicketModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleCreateTicket}
        isDark={isDark}
        ticketCount={tickets.length}
      />

      <ViewTicketModal
        isOpen={!!viewingTicket}
        ticket={viewingTicket}
        onClose={() => setViewingTicket(null)}
        isDark={isDark}
      />

      <UpdateStatusModal
        isOpen={!!updatingTicket}
        ticket={updatingTicket}
        onClose={() => setUpdatingTicket(null)}
        onSaved={handleStatusSaved}
        isDark={isDark}
      />
    </div>
  );
}
